import React from 'react';
import { FileText, Building2, Briefcase, Shield, CheckCircle, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import Newsletter from '../components/Newsletter';
import { services } from '../mockData';

const Services = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-20 overflow-hidden bg-gradient-to-br from-black via-[#1a1a1a] to-black">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 right-10 w-96 h-96 bg-[#C9A55A]/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="luxury-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-4 sm:mb-6">
            Our <span className="text-[#C9A55A]">Services</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto px-4">
            Comprehensive solutions for government, enterprise, and high-net-worth clients worldwide
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-16">
            {services.map((service, index) => (
              <Link key={service.id} to={service.link} className="block">
                <Card className="luxury-card h-full bg-gradient-to-br from-white/5 to-white/[0.02] border-white/10 hover:border-[#C9A55A]/50 group cursor-pointer">
                  <CardContent className="p-6 sm:p-8 md:p-10">
                    <div className="flex items-start justify-between mb-4 sm:mb-6">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#C9A55A]/10 rounded-lg flex items-center justify-center flex-shrink-0 border border-[#C9A55A]/20 group-hover:bg-[#C9A55A]/20 smooth-transition">
                        <span className="text-[#C9A55A] text-xl sm:text-2xl font-bold">{index + 1}</span>
                      </div>
                      <ArrowRight className="text-[#C9A55A] opacity-0 group-hover:opacity-100 smooth-transition" size={20} />
                    </div>
                    
                    <h3 className="luxury-heading text-xl sm:text-2xl md:text-3xl text-white mb-2 sm:mb-3">{service.title}</h3>
                    <p className="text-[#C9A55A] text-xs sm:text-sm mb-2 sm:mb-3 font-medium">{service.shortDesc}</p>
                    <p className="text-gray-300 text-sm sm:text-base leading-relaxed mb-4 sm:mb-6">{service.description}</p>
                    
                    <div className="border-t border-white/10 pt-4 sm:pt-6">
                      <h4 className="text-xs sm:text-sm font-semibold text-white mb-3 sm:mb-4 uppercase tracking-wider">Key Capabilities</h4>
                      <div className="space-y-2 sm:space-y-3">
                        {service.capabilities.slice(0, 4).map((capability, idx) => (
                          <div key={idx} className="flex items-start space-x-2 sm:space-x-3">
                            <CheckCircle className="text-[#C9A55A] flex-shrink-0 mt-0.5" size={14} />
                            <span className="text-gray-400 text-xs sm:text-sm leading-relaxed">{capability}</span>
                          </div>
                        ))}
                      </div>
                      
                      <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-white/10">
                        <span className="text-[#C9A55A] text-xs sm:text-sm font-medium group-hover:underline">
                          Learn More →
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 sm:py-16 md:py-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="luxury-heading text-3xl sm:text-4xl md:text-5xl text-white mb-4 sm:mb-6">
              Why Choose Syndicate
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-400 max-w-3xl mx-auto px-4">
              Experience, discretion, and global reach that delivers results
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            <Card className="standard-card bg-gradient-to-br from-white/5 to-white/[0.02] border-white/10">
              <CardContent className="p-6 sm:p-8 text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#C9A55A]/10 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 border-2 border-[#C9A55A]/20">
                  <span className="luxury-heading text-2xl sm:text-3xl text-[#C9A55A]">10+</span>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-white mb-2 sm:mb-3">Years of Excellence</h3>
                <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
                  Over a decade of proven success in international procurement and strategic advisory
                </p>
              </CardContent>
            </Card>

            <Card className="standard-card bg-gradient-to-br from-white/5 to-white/[0.02] border-white/10">
              <CardContent className="p-6 sm:p-8 text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#C9A55A]/10 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 border-2 border-[#C9A55A]/20">
                  <span className="luxury-heading text-2xl sm:text-3xl text-[#C9A55A]">4</span>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-white mb-2 sm:mb-3">Global Markets</h3>
                <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
                  Active operations across Canada, Middle East, USA, and Europe
                </p>
              </CardContent>
            </Card>

            <Card className="standard-card bg-gradient-to-br from-white/5 to-white/[0.02] border-white/10">
              <CardContent className="p-6 sm:p-8 text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#C9A55A]/10 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 border-2 border-[#C9A55A]/20">
                  <span className="luxury-heading text-2xl sm:text-3xl text-[#C9A55A]">100%</span>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-white mb-2 sm:mb-3">Client Satisfaction</h3>
                <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
                  Trusted by government ministries and high-net-worth clients worldwide
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-12 sm:py-16 md:py-20 overflow-hidden bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="luxury-heading text-3xl sm:text-4xl md:text-5xl text-white mb-4 sm:mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-6 sm:mb-8 leading-relaxed px-4">
            Connect with our team for a confidential consultation
          </p>
          <Button 
            asChild
            className="luxury-button bg-[#C9A55A] text-black hover:bg-[#B8941F] font-semibold text-base sm:text-lg px-8 sm:px-12 py-5 sm:py-7">
            <Link to="/contact">Contact Us Today</Link>
          </Button>
        </div>
      </section>

      <Newsletter />
    </div>
  );
};

export default Services;